import { useCallback, useEffect, useState } from 'react';
import { api, uploadFile } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { DistributorFundRequestDialog } from '../components/distributor/DistributorFundRequestDialog';
import { Button } from '../components/ui/button';
import { Wallet, ArrowClockwise, CheckCircle, XCircle, Clock } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_BASE = window.location.origin;

const PAYMENT_MODES = ['CDM', 'Branch Deposit', 'Online Transfer', 'QR Code'];
const BANK_LIST = [
  'Union Bank of India',
  'HDFC ***5678',
  'PNB 014',
  'RBL Bank',
  'Bandhan Bank',
  'Bank of Maharashtra',
  'IDBI',
];
const ONLINE_TRANSFER_ACCOUNTS = [
  'HDFC ****8043',
  'HDFC 7046',
  'HDFC 3497',
];

function fmt(n) {
  if (typeof n !== 'number') n = Number(n || 0);
  return '₹' + n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtDate(s) {
  if (!s) return '—';
  try { return new Date(s).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' }); }
  catch { return s; }
}

const TODAY_ISO = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });

export default function VendorFundRequestsPage() {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  // (legacy: vendor saved-accounts — kept for compat but not used in new flow)
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const initialForm = {
    amount: '',
    payment_mode: '',
    bank_name: '',
    account_info: '',
    reference_number: '',
    deposit_date: TODAY_ISO,
    screenshot_url: '',
    notes: '',
  };
  const [formData, setFormData] = useState(initialForm);
  const resetForm = () => setFormData(initialForm);

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const [r, l] = await Promise.all([
        api.get(`/vendors/${user.id}/fund-requests`),
        api.get(`/vendors/${user.id}/ledger`),
      ]);
      setRequests(r.data || []);
      setBalance(l.data?.balance);
    } catch (err) { toast.error('Load failed'); }
    finally { setLoading(false); }
  }, [user?.id]);

  useEffect(() => { load(); }, [load]);

  const handleScreenshot = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await uploadFile(fd);
      setFormData(p => ({ ...p, screenshot_url: res.data.url }));
      toast.success('Receipt uploaded');
    } catch (err) {
      toast.error('Upload fail');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmitFundRequest = async (e) => {
    e.preventDefault();
    const amt = parseFloat(formData.amount);
    if (!amt || amt <= 0) { toast.error('Valid amount daalein'); return; }
    if (!formData.payment_mode) { toast.error('Payment mode select karein'); return; }
    if ((formData.payment_mode === 'CDM' || formData.payment_mode === 'Branch Deposit') && !formData.bank_name) {
      toast.error('Bank select karein'); return;
    }
    if (formData.payment_mode === 'Online Transfer' && !formData.account_info) {
      toast.error('Transfer account select karein'); return;
    }
    setSubmitting(true);
    try {
      await api.post(`/vendors/${user.id}/fund-request`, {
        amount: amt,
        payment_mode: formData.payment_mode,
        bank_name: formData.bank_name,
        account_info: formData.account_info,
        reference_number: formData.reference_number,
        deposit_date: formData.deposit_date,
        screenshot_url: formData.screenshot_url,
        notes: formData.notes,
      });
      toast.success('Fund Request submitted — Admin approval ke baad balance credit hoga');
      setDialogOpen(false);
      resetForm();
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  const byStatus = requests.reduce((a, r) => { a[r.status] = (a[r.status] || 0) + 1; return a; }, {});

  return (
    <div className="space-y-5" data-testid="vendor-fund-requests-page">
      <div className="flex items-center gap-2 flex-wrap">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Wallet size={26} weight="duotone" className="text-indigo-600" /> Fund Add Request
        </h1>
        <div className="ml-auto flex gap-2 md:mr-14">
          <Button size="sm" variant="outline" onClick={load}><ArrowClockwise size={14} /> Refresh</Button>
          <DistributorFundRequestDialog
            dialogOpen={dialogOpen}
            setDialogOpen={setDialogOpen}
            resetForm={resetForm}
            formData={formData}
            setFormData={setFormData}
            PAYMENT_MODES={PAYMENT_MODES}
            BANK_LIST={BANK_LIST}
            ONLINE_TRANSFER_ACCOUNTS={ONLINE_TRANSFER_ACCOUNTS}
            handleSubmitFundRequest={handleSubmitFundRequest}
            submitting={submitting}
            handleScreenshot={handleScreenshot}
            uploading={uploading}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
          <div className="text-[10px] uppercase text-emerald-700 font-semibold">Current Balance (owed to you)</div>
          <div className="text-xl font-bold text-emerald-900">{fmt(balance?.balance || 0)}</div>
        </div>
        {[
          ['pending', 'Pending', 'amber'],
          ['approved', 'Approved', 'emerald'],
          ['rejected', 'Rejected', 'red'],
        ].map(([k, label, c]) => (
          <div key={k} className={`bg-${c}-50 border border-${c}-200 rounded-lg p-3`}>
            <div className={`text-[10px] uppercase text-${c}-700 font-semibold`}>{label}</div>
            <div className={`text-xl font-bold text-${c}-900`}>{byStatus[k] || 0}</div>
          </div>
        ))}
      </div>

      <div className="bg-white border rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b font-semibold text-gray-900">My Fund Requests ({requests.length})</div>
        {loading ? <div className="p-8 text-center text-sm text-gray-500">Loading...</div>
        : requests.length === 0 ? <div className="p-8 text-center text-sm text-gray-500">Koi fund request nahi. Upar "New Fund Request" pe click karein.</div>
        : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[11px] uppercase text-gray-500">
                <tr>
                  <th className="px-3 py-2 text-left">Date</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                  <th className="px-3 py-2 text-left">Payment Mode</th>
                  <th className="px-3 py-2 text-left">Bank / Account</th>
                  <th className="px-3 py-2 text-left">Reference</th>
                  <th className="px-3 py-2 text-left">Receipt</th>
                  <th className="px-3 py-2 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((r) => (
                  <tr key={r.id} className="border-t" data-testid={`fund-request-${r.id}`}>
                    <td className="px-3 py-2 text-xs">
                      {r.deposit_date || fmtDate(r.created_at)}
                      <div className="text-[10px] text-gray-400">{fmtDate(r.created_at)}</div>
                    </td>
                    <td className="px-3 py-2 text-right font-semibold text-indigo-700">{fmt(r.amount)}</td>
                    <td className="px-3 py-2 text-xs">{r.payment_mode || (r.method || '').replace('_', ' ')}</td>
                    <td className="px-3 py-2 text-xs">
                      {r.bank_name && <div className="font-semibold">{r.bank_name}</div>}
                      {r.account_info && <div className="text-gray-600 font-mono text-[11px]">{r.account_info}</div>}
                      {!r.bank_name && !r.account_info && '—'}
                    </td>
                    <td className="px-3 py-2 text-xs font-mono">{r.reference_number || '—'}</td>
                    <td className="px-3 py-2 text-xs">
                      {r.screenshot_url ? (
                        <a href={`${API_BASE}${r.screenshot_url}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                          View
                        </a>
                      ) : '—'}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded inline-flex gap-1 items-center ${
                        r.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                        r.status === 'rejected' ? 'bg-red-100 text-red-700' :
                        'bg-amber-100 text-amber-700'
                      }`}>
                        {r.status === 'approved' ? <CheckCircle size={10} /> : r.status === 'rejected' ? <XCircle size={10} /> : <Clock size={10} />}
                        {r.status?.toUpperCase()}
                      </span>
                      {r.reviewed_at && <div className="text-[10px] text-gray-500 mt-0.5">{fmtDate(r.reviewed_at)}</div>}
                      {r.review_notes && <div className="text-[10px] text-gray-500 mt-0.5">{r.review_notes}</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
